import { Box, styled, Typography } from '@mui/material';
import MUIStep from '@mui/material/Step';
import MUIStepButton from '@mui/material/StepButton';
import MUIStepConnector, {
  stepConnectorClasses,
} from '@mui/material/StepConnector';
import MUIStepIcon from '@mui/material/StepIcon';
import { stepLabelClasses } from '@mui/material/StepLabel';
import MUIStepper, {
  StepperProps as MUIStepperProps,
} from '@mui/material/Stepper';
import { ReactNode, useMemo } from 'react';
import { makeStyles } from 'tss-react/mui';

import { CheckFilledIcon } from '@/components/icons/CheckFilledIcon';
import { CheckIcon } from '@/components/icons/CheckIcon';
import { COLORS } from '@/styles/tokens/colors';
import { FONT_WEIGHTS } from '@/styles/tokens/fonts';

export enum StepTrackerStatus {
  Inactive = 'Inactive',
  Active = 'Active',
  Complete = 'Complete',
}

export enum StepTrackerVariant {
  Default = 'Default',
  Large = 'Large',
}

// Resolved `Large` variant colors passed down to segments. `*Stroke` colors
// apply to the icon/number, `*Background` colors to the circle, and `connector`
// to the line between steps.
interface StepTrackerColors {
  activeStroke: string;
  activeBackground: string;
  completeStroke: string;
  completeBackground: string;
  inactiveStroke: string;
  inactiveBackground: string;
  connector: string;
}

export interface SegmentProps {
  display: string;
  onClick?: () => void;
  status: StepTrackerStatus;
  /**
   * Icon shown in place of the number when the tracker uses the `Large`
   * variant. Ignored by the `Default` variant, and by `Complete` segments
   * (which always render a checkmark). The icon inherits the circle's color
   * via `currentColor`, so it does not need its own color set.
   */
  icon?: ReactNode;
}

export interface StepTrackerProps {
  segments: SegmentProps[];
  activeSegmentIndex: number;
  variant?: StepTrackerVariant;
  /**
   * When true, segments render as non-interactive (their `onClick` handlers are
   * ignored), turning the tracker into a display-only progress indicator.
   * Defaults to false.
   */
  disableNavigation?: boolean;
  /**
   * Per-status color overrides for the `Large` variant. `*Stroke` colors apply
   * to the icon/number, `*Background` colors to the circle, and the connector
   * color to the line between steps. Any omitted value falls back to the Figma
   * default. All are ignored by the `Default` variant.
   */
  largeActiveStrokeColor?: string;
  largeActiveBackgroundColor?: string;
  largeCompleteStrokeColor?: string;
  largeCompleteBackgroundColor?: string;
  largeInactiveStrokeColor?: string;
  largeInactiveBackgroundColor?: string;
  largeConnectorColor?: string;
}

// Figma defaults for the Large variant, used whenever an override is not
// supplied. Complete steps swap the segment icon for a checkmark. The Large
// variant is light-mode only for now (no dark spec exists).
// Fixed width for each step in the Large variant, sized so the connector lines
// match the Figma spacing (32px circle + ~8px gaps + ~40px line ≈ this width,
// with extra room so longer labels don't crowd the connectors).
const LARGE_STEP_MIN_WIDTH = 128;

const DEFAULT_LARGE_COLORS: StepTrackerColors = {
  activeStroke: COLORS.BLUE[900],
  activeBackground: COLORS.BLUE[200],
  completeStroke: COLORS.PRIMITIVES.WHITE,
  completeBackground: COLORS.BLUE[500],
  inactiveStroke: COLORS.BLUE[500],
  inactiveBackground: COLORS.BLUE[100],
  connector: COLORS.BLUE[100],
};

const useSegmentStyles = makeStyles<{
  status: StepTrackerStatus;
  content: string;
  isLarge: boolean;
}>()((theme, { status, content, isLarge }) => {
  const isDark = theme.palette.mode === 'dark';

  const primaryColor = isDark ? COLORS.PRIMITIVES.WHITE : COLORS.TEXT.PRIMARY;
  const inactiveColor = isDark ? COLORS.NAVY[400] : COLORS.TEXT.SECONDARY;

  const COLOR_MAP: Record<StepTrackerStatus, string> = {
    // The Large variant keeps every label at the primary text color to match
    // the Figma design, rather than dimming upcoming steps.
    [StepTrackerStatus.Inactive]: isLarge ? primaryColor : inactiveColor,
    [StepTrackerStatus.Active]: primaryColor,
    [StepTrackerStatus.Complete]: primaryColor,
  };

  return {
    text: {
      color: COLOR_MAP[status],
      // The Large variant keeps a constant medium weight and never wraps, per
      // the Figma. The Default variant bolds the active step, so it reserves the
      // bold width up front (via a hidden ::before) to avoid layout shift.
      fontWeight: isLarge ? FONT_WEIGHTS.medium : FONT_WEIGHTS.bold,
      ...(isLarge
        ? { whiteSpace: 'nowrap' }
        : {
            '&::before': {
              display: 'block',
              content: `"${content}"`,
              fontWeight: FONT_WEIGHTS.bold,
              height: 0,
              overflow: 'hidden',
              visibility: 'hidden',
            },
          }),
    },
  };
});

function CircleIcon({
  idx,
  status,
}: {
  idx: number;
  status: StepTrackerStatus;
}) {
  const BG_COLOR_MAP: Record<StepTrackerStatus, string> = {
    [StepTrackerStatus.Inactive]: COLORS.NAVY[400],
    [StepTrackerStatus.Active]: COLORS.ORANGE[400],
    [StepTrackerStatus.Complete]: COLORS.PRIMITIVES.WHITE,
  };

  return (
    <Box
      width="20px"
      height="20px"
      borderRadius="50%"
      bgcolor={BG_COLOR_MAP[status]}
      display="flex"
      justifyContent="center"
      alignItems="center"
    >
      {status == StepTrackerStatus.Complete && (
        <CheckFilledIcon size={16} color={COLORS.TEAL[500]} />
      )}
      {status != StepTrackerStatus.Complete && (
        <Typography color={COLORS.PRIMITIVES.WHITE} variant="body-s">
          {idx + 1}
        </Typography>
      )}
    </Box>
  );
}

function LargeCircleIcon({
  idx,
  status,
  icon,
  colors,
}: {
  idx: number;
  status: StepTrackerStatus;
  icon?: ReactNode;
  colors: StepTrackerColors;
}) {
  const backgroundColor: Record<StepTrackerStatus, string> = {
    [StepTrackerStatus.Inactive]: colors.inactiveBackground,
    [StepTrackerStatus.Active]: colors.activeBackground,
    [StepTrackerStatus.Complete]: colors.completeBackground,
  };

  const strokeColor: Record<StepTrackerStatus, string> = {
    [StepTrackerStatus.Inactive]: colors.inactiveStroke,
    [StepTrackerStatus.Active]: colors.activeStroke,
    [StepTrackerStatus.Complete]: colors.completeStroke,
  };

  // The icon inherits the circle's `color` via `currentColor`, so we only set
  // the stroke color once, on the wrapping circle.
  let content: ReactNode;
  if (status === StepTrackerStatus.Complete) {
    content = <CheckIcon size={16} />;
  } else if (icon) {
    content = icon;
  } else {
    content = <Typography variant="body-s">{idx + 1}</Typography>;
  }

  return (
    <Box
      width="32px"
      height="32px"
      borderRadius="50%"
      display="flex"
      justifyContent="center"
      alignItems="center"
      bgcolor={backgroundColor[status]}
      color={strokeColor[status]}
    >
      {content}
    </Box>
  );
}

// Horizontal connector for the Large variant. Positioned at the vertical center
// of the 32px circle (top: 16px) — not the circle+label stack — so the line
// stays level with the circles regardless of label height. The 24px horizontal
// inset is the circle radius (16px) plus the Figma's 8px gap on each side.
const LargeStepConnector = styled(MUIStepConnector, {
  shouldForwardProp: (prop) => prop !== 'lineColor',
})<{ lineColor: string }>(({ lineColor }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: '16px',
    left: 'calc(-50% + 24px)',
    right: 'calc(50% + 24px)',
  },
  [`& .${stepConnectorClasses.line}`]: {
    borderColor: lineColor,
    borderTopWidth: '1px',
  },
}));

function Segment({
  display,
  onClick,
  status,
  idx,
  variant,
  icon,
  colors,
}: SegmentProps & {
  idx: number;
  variant: StepTrackerVariant;
  colors: StepTrackerColors;
}) {
  const isLarge = variant === StepTrackerVariant.Large;

  const { classes, cx } = useSegmentStyles({
    status,
    content: display,
    isLarge,
  });

  const circle = isLarge ? (
    <LargeCircleIcon idx={idx} status={status} icon={icon} colors={colors} />
  ) : (
    <CircleIcon idx={idx} status={status} />
  );

  return (
    <MUIStepButton
      color="inherit"
      onClick={onClick}
      disabled={!onClick}
      icon={<MUIStepIcon icon={circle} />}
    >
      <Typography
        variant={isLarge ? 'body-xs' : 'body-s'}
        className={cx(classes.text)}
      >
        {display}
      </Typography>
    </MUIStepButton>
  );
}

export function StepTracker(
  // Omit `variant`: MUIStepperProps carries its own (Paper's elevation/outlined)
  // variant that would otherwise collide with our StepTrackerVariant prop. The
  // color overrides are named `large*Color`, so they don't collide with MUI's
  // ReactNode `connector`, which stays available for the Default variant.
  props: StepTrackerProps & Partial<Omit<MUIStepperProps, 'variant'>>
) {
  const {
    segments,
    activeSegmentIndex,
    variant = StepTrackerVariant.Default,
    disableNavigation = false,
    largeActiveStrokeColor,
    largeActiveBackgroundColor,
    largeCompleteStrokeColor,
    largeCompleteBackgroundColor,
    largeInactiveStrokeColor,
    largeInactiveBackgroundColor,
    largeConnectorColor,
    sx: stepperSx,
    ...muiStepperProps
  } = props;

  const isLarge = variant === StepTrackerVariant.Large;

  const largeColors: StepTrackerColors = useMemo(
    () => ({
      activeStroke: largeActiveStrokeColor ?? DEFAULT_LARGE_COLORS.activeStroke,
      activeBackground:
        largeActiveBackgroundColor ?? DEFAULT_LARGE_COLORS.activeBackground,
      completeStroke:
        largeCompleteStrokeColor ?? DEFAULT_LARGE_COLORS.completeStroke,
      completeBackground:
        largeCompleteBackgroundColor ?? DEFAULT_LARGE_COLORS.completeBackground,
      inactiveStroke:
        largeInactiveStrokeColor ?? DEFAULT_LARGE_COLORS.inactiveStroke,
      inactiveBackground:
        largeInactiveBackgroundColor ?? DEFAULT_LARGE_COLORS.inactiveBackground,
      connector: largeConnectorColor ?? DEFAULT_LARGE_COLORS.connector,
    }),
    [
      largeActiveStrokeColor,
      largeActiveBackgroundColor,
      largeCompleteStrokeColor,
      largeCompleteBackgroundColor,
      largeInactiveStrokeColor,
      largeInactiveBackgroundColor,
      largeConnectorColor,
    ]
  );

  return (
    <Box>
      <MUIStepper
        nonLinear
        activeStep={activeSegmentIndex}
        alternativeLabel={isLarge}
        connector={
          isLarge ? (
            <LargeStepConnector lineColor={largeColors.connector} />
          ) : null
        }
        sx={[
          // Figma pairs the circle and label with an 8px gap; MUI's default
          // alternative-label margin is 16px, so tighten it for the Large
          // variant to match the design.
          ...(isLarge
            ? [
                {
                  [`& .${stepLabelClasses.label}.${stepLabelClasses.alternativeLabel}`]:
                    {
                      marginTop: '8px',
                    },
                },
              ]
            : []),
          ...(Array.isArray(stepperSx) ? stepperSx : [stepperSx]),
        ]}
        {...muiStepperProps}
      >
        {segments.map((segment, idx) => (
          <MUIStep
            sx={{
              paddingLeft: idx === 0 ? 0 : undefined,
              paddingRight: idx === segments.length - 1 ? 0 : undefined,
              // Give Large steps a fixed width so the connector lines spread to
              // the Figma spacing (~40px gap between nodes) instead of collapsing
              // to the label width.
              ...(isLarge ? { minWidth: LARGE_STEP_MIN_WIDTH } : {}),
            }}
            key={`${segment.display}-${idx}`}
            completed={segment.status == StepTrackerStatus.Complete}
          >
            <Segment
              display={segment.display}
              onClick={disableNavigation ? undefined : segment.onClick}
              status={segment.status}
              idx={idx}
              variant={variant}
              icon={segment.icon}
              colors={largeColors}
            />
          </MUIStep>
        ))}
      </MUIStepper>
    </Box>
  );
}
